import { forwardRef, ComponentPropsWithoutRef, CSSProperties, createContext, useContext } from 'react'
import { useDialogState } from '@/hooks'
import { getDisplaySegments } from '@/store/utils'
import type { TextSegment } from '@/store/types'

/**
 * TextBox Context - 用于在子组件间共享状态
 */
interface TextBoxContextValue {
  speaker: string | undefined
  visibleSegments: TextSegment[]
  isTyping: boolean
  showCursor: boolean
}

const TextBoxContext = createContext<TextBoxContextValue | null>(null)

const useTextBoxContext = () => {
  const context = useContext(TextBoxContext)
  if (!context) {
    throw new Error('TextBox components must be used within TextBox.Root')
  }
  return context
}

/**
 * TextBox Root 组件
 * Headless 文本框根组件
 */
interface TextBoxRootProps extends ComponentPropsWithoutRef<'div'> {
  showCursor?: boolean
}

const TextBoxRoot = forwardRef<HTMLDivElement, TextBoxRootProps>(
  ({ showCursor = true, children, ...props }, ref) => {
    const state = useDialogState()

    if (!state.isVisible || !state.currentDialogue) {
      return null
    }

    const visibleSegments = getDisplaySegments(
      state.displaySegments,
      state.currentTextLength
    )

    const contextValue: TextBoxContextValue = {
      speaker: state.currentDialogue.speaker,
      visibleSegments,
      isTyping: state.isTyping,
      showCursor,
    }

    return (
      <TextBoxContext.Provider value={contextValue}>
        <div ref={ref} {...props}>
          {children}
        </div>
      </TextBoxContext.Provider>
    )
  }
)

TextBoxRoot.displayName = 'TextBox.Root'

/**
 * TextBox Speaker 组件
 * 显示说话人名字
 */
interface TextBoxSpeakerProps extends ComponentPropsWithoutRef<'div'> {}

const TextBoxSpeaker = forwardRef<HTMLDivElement, TextBoxSpeakerProps>(
  ({ children, ...props }, ref) => {
    const { speaker } = useTextBoxContext()

    if (!speaker) {
      return null
    }

    return (
      <div ref={ref} {...props}>
        {children || speaker}
      </div>
    )
  }
)

TextBoxSpeaker.displayName = 'TextBox.Speaker'

/**
 * TextBox Content 组件
 * 显示对话内容文本
 * 
 * 注意：此组件直接返回片段，不包裹额外容器，以便与 Cursor 保持在同一行
 */
interface TextBoxContentProps {
  renderSegment?: (segment: TextSegment, index: number) => React.ReactNode
}

const TextBoxContent = ({ renderSegment }: TextBoxContentProps) => {
  const { visibleSegments } = useTextBoxContext()
  const state = useDialogState()

  const defaultRenderSegment = (segment: TextSegment, index: number) => {
    if (segment.effect?.type) {
      const repeatFromSegment = segment.effect.repeat
      const repeatFromConfig = state.effectRepeats?.[segment.effect.type]
      const repeatCandidate =
        typeof repeatFromSegment === 'number' && repeatFromSegment > 0
          ? repeatFromSegment
          : repeatFromConfig
      const normalizedRepeat =
        typeof repeatCandidate === 'number' && repeatCandidate > 0
          ? repeatCandidate
          : undefined
      const effectStyle: CSSProperties | undefined = normalizedRepeat
        ? ({
            ['--dialog-text-iteration-count' as const]: normalizedRepeat,
          } as CSSProperties)
        : undefined

      return (
        <span
          key={index}
          data-effect={segment.effect.type}
          data-repeat={normalizedRepeat}
          style={effectStyle}
        >
          {segment.text}
        </span>
      )
    }
    return <span key={index}>{segment.text}</span>
  }

  const renderer = renderSegment || defaultRenderSegment

  return <>{visibleSegments.map((segment, index) => renderer(segment, index))}</>
}

TextBoxContent.displayName = 'TextBox.Content'

/**
 * TextBox Cursor 组件
 * 显示打字光标
 */
interface TextBoxCursorProps extends ComponentPropsWithoutRef<'span'> {}

const TextBoxCursor = forwardRef<HTMLSpanElement, TextBoxCursorProps>(
  ({ children, ...props }, ref) => {
    const { isTyping, showCursor } = useTextBoxContext()

    if (!isTyping || !showCursor) {
      return null
    }

    return (
      <span ref={ref} {...props}>
        {children || '_'}
      </span>
    )
  }
)

TextBoxCursor.displayName = 'TextBox.Cursor'

export const TextBox = {
  Root: TextBoxRoot,
  Speaker: TextBoxSpeaker,
  Content: TextBoxContent,
  Cursor: TextBoxCursor,
}
